import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { supabase } from '../supabaseClient';

const AuthContext = createContext(null);

const API = process.env.REACT_APP_API_URL || '';

// Axios interceptor: automatically attach the Supabase access token as a
// Bearer header on every request so the server can verify it.
let _currentAccessToken = null;

axios.interceptors.request.use(config => {
  if (_currentAccessToken) {
    config.headers = config.headers ?? {};
    if (!config.headers['Authorization']) {
      config.headers['Authorization'] = `Bearer ${_currentAccessToken}`;
    }
  }
  return config;
});

export function AuthProvider({ children }) {
  const [user, setUser]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [accessToken, setAccessToken] = useState(null);

  // needsUsername is derived directly from the user object to avoid race
  // conditions between two separate useState updates. When user is set,
  // needsUsername is always in sync.
  const needsUsername = user?.needsUsername ?? false;

  // Sync Supabase session → our backend user record.
  // Called whenever Supabase fires an auth state change.
  const syncUser = useCallback(async (session) => {
    if (!session?.access_token) {
      _currentAccessToken = null;
      setAccessToken(null);
      setUser(null);
      setLoading(false);
      return null;
    }

    _currentAccessToken = session.access_token;
    setAccessToken(session.access_token);

    try {
      const res = await axios.post(`${API}/auth/sync-user`, {}, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      // Embed needsUsername in the user object so they're always in sync
      // (a single setUser call = a single atomic render).
      setUser({ ...res.data.user, needsUsername: !!res.data.needsUsername });
      setLoading(false);
      return res.data;
    } catch (err) {
      console.error('[AuthContext] sync-user failed:', err.message);
      setUser(null);
      setLoading(false);
      return null;
    }
  }, []);

  useEffect(() => {
    // onAuthStateChange fires immediately with the current session on mount
    // (INITIAL_SESSION event), so no need for a separate getSession() call.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      syncUser(session);
    });

    return () => subscription.unsubscribe();
  }, [syncUser]);

  const logout = useCallback(async () => {
    await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
    _currentAccessToken = null;
    setAccessToken(null);
    setUser(null);
    axios.post(`${API}/auth/logout`).catch(() => {});
  }, []);

  // Use this after account deletion — the Supabase auth user is already gone
  // so signOut will 403, but we still call it (scope:local) to purge the
  // token from localStorage. Without this, the stale token lingers and
  // Supabase fires INITIAL_SESSION with it on the next OAuth callback,
  // which races with the new SIGNED_IN event and can clobber user state.
  const clearSession = useCallback(() => {
    supabase.auth.signOut({ scope: 'local' }).catch(() => {});
    _currentAccessToken = null;
    setAccessToken(null);
    setUser(null);
    axios.post(`${API}/auth/logout`).catch(() => {});
  }, []);

  // refreshUser re-fetches our backend user row (e.g. after profile update)
  const refreshUser = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return syncUser(session);
  }, [syncUser]);

  return (
    <AuthContext.Provider value={{ user, loading, logout, clearSession, refreshUser, accessToken, needsUsername }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export default AuthContext;
